import { useState } from "react";
import {
  ArrowLeftRight,
  Copy,
  Check,
  Loader2,
  ClipboardPaste,
  Download,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { LANG_META } from "@/lib/languages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

const MAX_CHARS = 5000;

// Google Translate free public endpoint (same one used by the classic
// jQuery translator snippet). No API key required — response format:
// [ [ [translatedChunk, srcChunk, ...], ... ], ... ]
const gtranslate = async (text, srcCode, tgtCode) => {
  const url =
    `https://translate.googleapis.com/translate_a/single?client=gtx` +
    `&sl=${encodeURIComponent(srcCode)}` +
    `&tl=${encodeURIComponent(tgtCode)}` +
    `&dt=t&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const chunks = Array.isArray(data?.[0]) ? data[0] : [];
  return chunks.map((c) => (c && c[0]) || "").join("");
};

const LANG_KEYS = Object.keys(LANG_META);

const IconAction = ({ label, onClick, disabled, testId, children, active }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          data-testid={testId}
          aria-label={label}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent transition-colors ${
            disabled
              ? "text-muted-foreground/40 cursor-not-allowed"
              : "text-muted-foreground hover:text-primary hover:bg-secondary"
          } ${active ? "text-primary" : ""}`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const downloadText = (text, filename) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const Translator = ({
  defaultSource = "english",
  defaultTarget = "hindi",
  lockPair = false,
}) => {
  const [source, setSource] = useState(defaultSource);
  const [target, setTarget] = useState(defaultTarget);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedIn, setCopiedIn] = useState(false);
  const [copiedOut, setCopiedOut] = useState(false);

  const swap = () => {
    if (lockPair) return;
    setSource(target);
    setTarget(source);
    setInput(output);
    setOutput(input);
  };

  const handleTranslate = async () => {
    const text = input.trim();
    if (!text) {
      toast.error("Please enter some text to translate.");
      return;
    }
    if (source === target) {
      setOutput(text);
      return;
    }
    setLoading(true);
    setOutput("");
    try {
      const srcCode = LANG_META[source].code;
      const tgtCode = LANG_META[target].code;
      const translated = await gtranslate(text, srcCode, tgtCode);
      setOutput(translated);
    } catch (e) {
      toast.error("Translation failed. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const doCopy = async (which) => {
    const text = which === "in" ? input : output;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (which === "in") {
        setCopiedIn(true);
        setTimeout(() => setCopiedIn(false), 1500);
      } else {
        setCopiedOut(true);
        setTimeout(() => setCopiedOut(false), 1500);
      }
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Unable to copy");
    }
  };

  const doPaste = async (which) => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        toast.error("Clipboard is empty");
        return;
      }
      if (which === "in") {
        setInput(text.slice(0, MAX_CHARS));
      } else {
        setOutput(text);
      }
      toast.success("Pasted from clipboard");
    } catch {
      toast.error("Clipboard permission denied");
    }
  };

  const doDownload = (which) => {
    const text = which === "in" ? input : output;
    const lang = which === "in" ? source : target;
    if (!text) {
      toast.error("Nothing to download");
      return;
    }
    downloadText(text, `translation-${lang}.txt`);
    toast.success("Downloaded");
  };

  const doReset = (which) => {
    if (which === "in") setInput("");
    else setOutput("");
  };

  const Toolbar = ({ which }) => {
    const has = which === "in" ? !!input : !!output;
    return (
      <div className="flex items-center gap-1" data-testid={`toolbar-${which}`}>
        <IconAction
          label="Copy"
          testId={`copy-${which}-button`}
          onClick={() => doCopy(which)}
          disabled={!has}
          active={which === "in" ? copiedIn : copiedOut}
        >
          {(which === "in" ? copiedIn : copiedOut) ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" strokeWidth={1.75} />
          )}
        </IconAction>
        <IconAction
          label="Paste"
          testId={`paste-${which}-button`}
          onClick={() => doPaste(which)}
        >
          <ClipboardPaste className="h-4 w-4" strokeWidth={1.75} />
        </IconAction>
        <IconAction
          label="Download .txt"
          testId={`download-${which}-button`}
          onClick={() => doDownload(which)}
          disabled={!has}
        >
          <Download className="h-4 w-4" strokeWidth={1.75} />
        </IconAction>
        <IconAction
          label="Reset"
          testId={`reset-${which}-button`}
          onClick={() => doReset(which)}
          disabled={!has}
        >
          <RotateCcw className="h-4 w-4" strokeWidth={1.75} />
        </IconAction>
      </div>
    );
  };

  return (
    <div data-testid="translator-widget" className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border rounded-xl overflow-hidden border border-border shadow-sm">
        {/* Source */}
        <div className="bg-white p-5 md:p-6 min-h-[300px] flex flex-col focus-within:ring-2 focus-within:ring-primary/40 relative">
          <div className="flex items-center justify-between mb-3 gap-3">
            <Select
              value={source}
              onValueChange={setSource}
              disabled={lockPair}
            >
              <SelectTrigger
                data-testid="source-lang-select"
                className="w-[170px] border-0 shadow-none focus:ring-0 font-semibold text-sm p-0 h-auto"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_KEYS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {LANG_META[k].name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Toolbar which="in" />
          </div>
          <textarea
            data-testid="source-text-input"
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, MAX_CHARS))}
            placeholder={`Type or paste in ${LANG_META[source].name}…`}
            className="flex-1 resize-none outline-none text-base leading-relaxed placeholder:text-muted-foreground bg-transparent min-h-[140px]"
          />
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-mono">{LANG_META[source].native}</span>
            <span
              data-testid="char-count"
              className={input.length >= MAX_CHARS ? "text-destructive" : ""}
            >
              {input.length}/{MAX_CHARS}
            </span>
          </div>
        </div>

        {/* Target */}
        <div className="bg-white p-5 md:p-6 min-h-[300px] flex flex-col relative">
          <div className="flex items-center justify-between mb-3 gap-3">
            <Select
              value={target}
              onValueChange={setTarget}
              disabled={lockPair}
            >
              <SelectTrigger
                data-testid="target-lang-select"
                className="w-[170px] border-0 shadow-none focus:ring-0 font-semibold text-sm p-0 h-auto"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_KEYS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {LANG_META[k].name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Toolbar which="out" />
          </div>
          <div
            data-testid="translation-output"
            className="flex-1 whitespace-pre-wrap text-base leading-relaxed text-foreground overflow-auto min-h-[140px]"
          >
            {loading ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Translating…
              </span>
            ) : output ? (
              output
            ) : (
              <span className="text-muted-foreground">
                Your translation will appear here.
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-mono">{LANG_META[target].native}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col items-center gap-3">
        <Button
          data-testid="translate-button"
          onClick={handleTranslate}
          disabled={loading || !input.trim()}
          size="lg"
          className="h-12 px-12 text-base font-semibold rounded-lg shadow-md"
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Translate
        </Button>
        <div className="flex items-center gap-3">
          <Button
            data-testid="swap-button"
            onClick={swap}
            variant="outline"
            size="sm"
            disabled={lockPair}
            className="rounded-lg"
          >
            <ArrowLeftRight className="mr-2 h-4 w-4" strokeWidth={1.75} />
            Swap
          </Button>
          <p className="text-xs text-muted-foreground">
            Free • No signup • Powered by AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default Translator;
